import { useCallback, useRef } from 'react';
import type { DocumentFileSavedNotice, ForceReloadOptions } from '../realtime/documentCollaborationTypes';
import { isDocumentSavedByEditorSession } from './documentSaveNotice';

export interface UseDocumentFileSavedHandlerOptions {
  userAliases: Iterable<string>;
  userDisplayName?: string | null;
  editorSessionId?: string | null;
  requestDirtyState: () => Promise<boolean>;
  requestForceReload: (options?: ForceReloadOptions) => void;
  /** Sync "Saved X ago" with the server version timestamp from collaboration. */
  onCollaborationSaveApplied?: (savedAtUtc: string) => void;
  setNewerVersionBy: (name: string | null) => void;
  /** Invoked before an automatic peer reload (show shell loading overlay). */
  onPeerReloadStarting?: () => void;
  /** Current peer-lock state. A locked editor cannot contain legitimate local edits. */
  isPeerEditLocked?: () => boolean;
}

/**
 * Handles SignalR FileSaved: own saves update status in place; peer saves reload a clean editor or
 * show a banner when local edits exist. Reloading after our own save creates a second edit session
 * and can turn load-time control events into another autosave cycle.
 */
export function useDocumentFileSavedHandler({
  userAliases,
  userDisplayName,
  editorSessionId,
  requestDirtyState,
  requestForceReload,
  onCollaborationSaveApplied,
  setNewerVersionBy,
  onPeerReloadStarting,
  isPeerEditLocked
}: UseDocumentFileSavedHandlerOptions) {
  const onCollaborationSaveAppliedRef = useRef(onCollaborationSaveApplied);
  onCollaborationSaveAppliedRef.current = onCollaborationSaveApplied;
  const onPeerReloadStartingRef = useRef(onPeerReloadStarting);
  onPeerReloadStartingRef.current = onPeerReloadStarting;
  const isPeerEditLockedRef = useRef(isPeerEditLocked);
  isPeerEditLockedRef.current = isPeerEditLocked;
  const editorSessionIdRef = useRef(editorSessionId);
  const userAliasesRef = useRef(userAliases);
  const userDisplayNameRef = useRef(userDisplayName);

  editorSessionIdRef.current = editorSessionId;
  userAliasesRef.current = userAliases;
  userDisplayNameRef.current = userDisplayName;

  return useCallback(
    (notice: DocumentFileSavedNotice) => {
      const ownSave = isDocumentSavedByEditorSession(
        notice,
        editorSessionIdRef.current,
        userAliasesRef.current,
        { displayName: userDisplayNameRef.current }
      );

      const applyCollaborationTime = () => {
        onCollaborationSaveAppliedRef.current?.(notice.savedAtUtc);
      };

      if (ownSave) {
        setNewerVersionBy(null);
        applyCollaborationTime();
        return;
      }

      // FileSaved is broadcast before the saving tab releases its edit lock. While this tab is
      // peer-locked, the host is read-only and clears its dirty flags, so reload immediately. This
      // closes the hand-off race where the lock release enabled typing before the async dirty query
      // returned, leaving the second user on an obsolete edit session.
      if (isPeerEditLockedRef.current?.()) {
        onPeerReloadStartingRef.current?.();
        requestForceReload({ overlay: true });
        return;
      }

      void (async () => {
        const dirty = await requestDirtyState();
        if (dirty) {
          setNewerVersionBy(notice.savedByDisplayName || 'another user');
        } else {
          onPeerReloadStartingRef.current?.();
          requestForceReload({ overlay: true });
        }
      })();
    },
    [requestDirtyState, requestForceReload, setNewerVersionBy]
  );
}
